import { useEffect, useState, useSyncExternalStore } from 'react';

type SyncExternalListener<State extends {}> = (value: Array<keyof State>) => void;

interface UpdateCallback<T> {
    (prev: T, next: T): void;
}

/**
 * Warning: the return type Pick<State, Keys> is not strictly correct, because all other keys are still present in the object.
 */
function useSyncState<State extends {}, Keys extends keyof State>(store: SyncExternalStore<State>, keys: Array<Keys>): Pick<State, Keys> {
    const [ state, setState ] = useState<Pick<State, Keys>>(store.snapshot);

    useEffect(
        () => {
            setState(store.snapshot);
            return store.subscribe((changedKeys: Array<keyof State>) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                if (changedKeys.find((value: keyof State) => keys.includes(value as any))) {
                    setState(store.snapshot);
                }
            });
        },
        []
    );

    return state;
}

function useCompleteState<State extends {}>(store: SyncExternalStore<State>): State {
    const [ state, setState ] = useState(store.snapshot);

    useEffect(
        () => {
            setState(store.snapshot);
            return store.subscribe(() => {
                setState(store.snapshot);
            });
        },
        []
    );

    return state;
}

export class SyncExternalStore<State extends {}> {
    private listeners: Array<SyncExternalListener<State>> = [];
    protected internalState: State & { restored?: string; };
    public get initialState() {
        return this._initialState;
    }

    public useState = <Keys extends keyof State>(keys: Array<Keys>) => useSyncState<State, Keys>(this, keys);
    public useCompleteState = () => useCompleteState(this);

    /** in React.FC use useSyncState (hook) instead */
    public get snapshot(): State & { restored?: string; }{
        return this.internalState;
    }

    constructor(
        protected readonly _initialState: State,
        restoredState?: State,
    ) {
        this.internalState = { ..._initialState, ...restoredState };
        this.getSnapshot = this.getSnapshot.bind(this);
        this.subscribe = this.subscribe.bind(this);
        this.updateState = this.updateState.bind(this);
        this.emitChange = this.emitChange.bind(this);
        this._unsafeUpdateState = this._unsafeUpdateState.bind(this);
        this.resetState = this.resetState.bind(this);
    }

    public subscribe(listener: SyncExternalListener<State>): () => void {
        this.listeners = [...this.listeners, listener];
        return () => {
            this.listeners = this.listeners.filter(l => l !== listener);
        };
    }

    public updateState(state: Partial<State>, callback?: UpdateCallback<State>): void {
        const oldState = { ...this.internalState };
        this._unsafeUpdateState(state);
        callback?.(oldState, this.internalState);
    }

    public getSnapshot(): State {
        return this.internalState;
    }

    public _unsafeUpdateState(state: Partial<State>): void {
        this.internalState = {...this.internalState, ...state };
        this.emitChange(state);
    }

    protected emitChange(state: Partial<State>): void {
        for (const listener of this.listeners) {
            listener(Object.keys(state) as Array<(keyof State)>);
        }
    }

    /** use only for debug components */
    public hook = () => {
        return useSyncExternalStore<State>(this.subscribe, this.getSnapshot);
    };

    public waitFor = async (testCallback: (state: State) => boolean): Promise<void> => {
        if (testCallback(this.snapshot)) {
            return;
        }

        return new Promise<void>((resolve) => {
            const disposer = this.subscribe(() => {
                if (testCallback(this.snapshot)) {
                    disposer();
                    resolve();
                }
            });
        });
    };

    public resetState() {
        this.updateState(this.initialState);
    }
}

export class SelectionManager<T> extends SyncExternalStore<{ selection: ReadonlyArray<T>; enabled: boolean; }> {
    constructor() {
        super({
            selection: [],
            enabled: false,
        });
        this.setSelection = this.setSelection.bind(this);
        this.toggleSelection = this.toggleSelection.bind(this);
        this.useSelection = this.useSelection.bind(this);
        this.enable = this.enable.bind(this);
        this.disable = this.disable.bind(this);
    }

    public setSelection(selection: ReadonlyArray<T>) {
        this.updateState({ selection, enabled: true });
    }

    public enable(): void {
        this.updateState({enabled: true});
    }

    public toggleSelection(value: T): void {
        const currentSelection: ReadonlyArray<T> = this.snapshot.selection;
        if (currentSelection.indexOf(value) === -1) {
            this.updateState({selection: [...this.snapshot.selection, value]});
        } else {
            const selection: Array<T> = this.snapshot.selection.filter((selectionValue: T) => {
                return selectionValue !== value;
            });
            this.updateState({selection, enabled: selection.length !== 0});
        }
    }

    public useSelection(): ReadonlyArray<T> {
        return this.useState(['selection']).selection;
    }

    public disable(): void {
        this.updateState({ selection: [], enabled: false });
    }
}

export class ToggleManager<State extends {[P in keyof State]: boolean | undefined}> extends SyncExternalStore<Partial<State>> {
    public useList(): Array<keyof State> {
        const state = this.useCompleteState();
        const entries = Object.entries(state);
        return entries.filter(value => value[1]).map(value => value[0] as keyof State);
    }

    /** in FC use useList */
    public asList(): Array<keyof State> {
        const entries = Object.entries(this.snapshot);
        return entries.filter(value => value[1]).map(value => value[0] as keyof State);
    }

    public constructor() {
        super({});
        this.toggle = this.toggle.bind(this);
        this.setValues = this.setValues.bind(this);
    }

    public toggle(key: keyof State): void {
        this.updateState({
            [key]:  this.snapshot[key] === undefined || this.snapshot[key] === false
        } as Partial<State>);
    };

    public setValues(keys: Array<keyof State>, value: boolean): void {
        const partial: Partial<{[P in keyof State]: boolean}> = {};
        for (const key of keys) {
            partial[key] = value;
        }
        this.updateState(partial as Partial<State>);
    };

    public override resetState() {
        this.internalState = {};
        this.emitChange({});
    }
}